import React, { useEffect, useState } from "react";
import { MessageSquare } from "lucide-react";
import { FloatingBubble } from "../../common/floatingDock";
import { useRoomsUnread } from "../../hooks/comms/RoomsUnreadProvider";
import { ChatDockPanel } from "./ChatDockPanel";
import { CHAT_DOCK_ANCHOR_KEY } from "./chatDockKeys";
import { useChatDock } from "./ChatDockProvider";

/** Below this the panel goes full-screen and the bubble stops being draggable. */
const MOBILE_BREAKPOINT_PX = 640;

/**
 * Track the viewport against the mobile breakpoint.
 *
 * The panel branches its whole tree on this rather than hiding one variant with
 * CSS, matching how `UserListDashboard` handles its own mobile split — so a
 * phone never mounts the desktop card's chrome at all.
 *
 * @returns {boolean}
 */
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    () => window.innerWidth < MOBILE_BREAKPOINT_PX
  );

  useEffect(() => {
    const query = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX - 1}px)`);
    const onChange = (event) => setIsMobile(event.matches);
    query.addEventListener("change", onChange);
    setIsMobile(query.matches);
    return () => query.removeEventListener("change", onChange);
  }, []);

  return isMobile;
}

/**
 * The app-wide chat surface: a draggable launcher that opens into the roster,
 * a client's chat, or their notes.
 *
 * Replaces the old `UserDock`, which only existed inside the Users split view
 * and popped itself out whenever the coach left the Chat tab. This is mounted
 * once in `App.js` for every signed-in route instead, so the conversation is
 * always one click away and never appears uninvited.
 *
 * The unread badge reads `totalUnread` — the same value feeding the sidebar's
 * Users badge — so the two can never disagree.
 */
export function ChatDock() {
  const { totalUnread } = useRoomsUnread();
  const dock = useChatDock();
  const isMobile = useIsMobile();

  if (!dock.isOpen) {
    return (
      <FloatingBubble
        Icon={MessageSquare}
        count={totalUnread}
        label={totalUnread > 0 ? `Open chat, ${totalUnread} unread` : "Open chat"}
        onClick={dock.openDock}
        storageKey={CHAT_DOCK_ANCHOR_KEY}
        isMobile={isMobile}
      />
    );
  }

  return (
    <ChatDockPanel
      view={dock.view}
      clientId={dock.clientId}
      expanded={dock.expanded}
      isMobile={isMobile}
      onSelectClient={dock.openChatFor}
      onViewChange={dock.setView}
      onBack={dock.back}
      onToggleExpanded={dock.toggleExpanded}
      onClose={dock.close}
    />
  );
}

export default ChatDock;
